import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import topLogoImage from './assets/brokerlogo.png';
import { usePermissions } from './context/PermissionsContext';
import logoImage from './assets/FullLogo_1.png';

interface SidebarProps {
  isVisible: boolean;
  toggleSidebar: () => void;
}

interface User {
  id: number;
  username: string | null;
  email: string;
  role: string;
  broker_id?: number | null;
}

const Sidebar = ({ isVisible, toggleSidebar }: SidebarProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isInsurersDropdownOpen, setIsInsurersDropdownOpen] = useState(false);
  const [isManageDropdownOpen, setIsManageDropdownOpen] = useState(false);
  const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { can, isSuperAdmin } = usePermissions();
  const [brokerLogoUrl, setBrokerLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    // Get user from localStorage
    const loadUser = () => {
      console.log('🔍 Sidebar - loading user from localStorage');
      const storedUser = localStorage.getItem('user');
      console.log('🔍 storedUser:', storedUser);

      if (storedUser && storedUser !== 'undefined') {
        try {
          const parsedUser = JSON.parse(storedUser);
          console.log('🔍 parsedUser:', parsedUser);
          console.log('🔍 user role:', parsedUser.role);
          setUser(parsedUser);
        } catch (e) {
          console.error('Error parsing user from localStorage:', e);
          setUser(null);
        }
      } else {
        console.log('🔍 No valid user found in localStorage');
        setUser(null);
      }
      setIsLoading(false);
    };

    loadUser();

    // Add event listener for storage changes (in case user updates in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user') {
        loadUser();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const fetchBrokerLogo = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const res = await fetch(`${apiUrl}/api/broker-profile/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setBrokerLogoUrl(data?.logo_url ? `${apiUrl}${data.logo_url}` : null);
      } catch (err) {
        console.warn('Could not fetch broker logo:', err);
      }
    };

    fetchBrokerLogo();
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleInsurersDropdown = () => {
    setIsInsurersDropdownOpen(!isInsurersDropdownOpen);
  };

  const toggleManageDropdown = () => {
    setIsManageDropdownOpen(!isManageDropdownOpen);
  };

  const toggleAdminDropdown = () => {
    setIsAdminDropdownOpen(!isAdminDropdownOpen);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error('Failed to load image:', e);
  };

  const handleImageLoad = (type: string) => {
    // Image load handler if needed
  };

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  console.log('🔍 Sidebar render - user:', user);
  console.log('🔍 Sidebar render - isAdmin:', isAdmin);
  console.log('🔍 Sidebar render - isLoading:', isLoading);

  if (isLoading) {
    return (
      <div
        className={`fixed top-0 left-0 h-full w-64 bg-white shadow-md flex flex-col transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-[-100%]'
        }`}
      >
        <div className="p-4 border-b">
          <img
            src={brokerLogoUrl || topLogoImage}
            alt="Top Logo"
            className="max-w-full h-auto max-h-20 object-contain"
            onError={handleImageError}
            onLoad={() => handleImageLoad('Top Logo')}
          />
        </div>
        <div className="flex-1 p-4 flex items-center justify-center">
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`fixed top-0 left-0 h-full w-64 bg-white shadow-md flex flex-col transition-transform duration-300 ease-in-out ${
        isVisible ? 'translate-x-0' : 'translate-x-[-100%]'
      }`}
    >
      <div className="p-4 border-b">
        <img
          src={brokerLogoUrl || topLogoImage}
          alt="Top Logo"
          className="max-w-full h-auto max-h-20 object-contain"
          onError={handleImageError}
          onLoad={() => handleImageLoad('Top Logo')}
        />
      </div>
      <nav className="flex-1 p-4 overflow-y-auto">
        <ul className="space-y-2">
          {/* Dashboard — always visible */}
          <li>
            <Link
              to="/dashboard"
              className="flex items-center p-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              Dashboard
            </Link>
          </li>

          {/* Clients */}
          {can('client_info', 'read') && (
            <li>
              <Link
                to="/clients"
                className="flex items-center p-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Clients
              </Link>
            </li>
          )}

          {/* Consultants */}
          {can('consultants', 'read') && (
            <li>
              <Link
                to="/consultants"
                className="flex items-center p-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Consultants
              </Link>
            </li>
          )}

          {/* Admin Section — visible to super admins, broker admins, and delegated permission-admins */}
          {isAdmin && (isSuperAdmin || !!user?.broker_id || can('manage_permissions', 'read')) && (
            <li>
              <button
                onClick={toggleAdminDropdown}
                className="flex items-center justify-between w-full p-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                <span className="flex items-center">
                  <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  Admin
                </span>
                {isAdminDropdownOpen ? (
                  <ChevronUpIcon className="w-5 h-5" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5" />
                )}
              </button>
              {isAdminDropdownOpen && (
                <ul className="ml-4 mt-2 space-y-1">
                  <li>
                    <Link
                      to="/users"
                      className="flex items-center p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      User Management
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/admin/permissions"
                      className="flex items-center p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Permissions
                    </Link>
                  </li>

                </ul>
              )}
            </li>
          )}

          {/* Manage Dropdown — show only if at least one child is permitted */}
          {(can('instructions', 'read') ||
            can('claims', 'read') ||
            can('quotes', 'read') ||
            can('renewals', 'read') ||
            can('return_debits', 'read')) && (
            <li>
              <button
                onClick={toggleManageDropdown}
                className="flex items-center justify-between w-full p-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Manage
                {isManageDropdownOpen ? (
                  <ChevronUpIcon className="w-5 h-5" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5" />
                )}
              </button>
              {isManageDropdownOpen && (
                <ul className="ml-4 mt-2 space-y-1">
                  {can('instructions', 'read') && (
                    <li>
                      <Link
                        to="/instructions"
                        className="flex items-center p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        Instructions
                      </Link>
                    </li>
                  )}

                  {can('claims', 'read') && (
                    <li>
                      <Link
                        to="/claims"
                        className="flex items-center p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        Claims
                      </Link>
                    </li>
                  )}

                  {/* Hidden per stakeholder request — uncomment to restore
                  {can('claims', 'read') && (
                    <li>
                      <Link
                        to="/claims-intake"
                        className="flex items-center p-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        Claims Intake
                      </Link>
                    </li>
                  )}
                  */}

                  {can('quotes', 'read') && (
                    <li>
                      <Link
                        to="/quotations"
                        className="flex items-center p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        Quotations
                      </Link>
                    </li>
                  )}

                  {can('renewals', 'read') && (
                    <li>
                      <Link
                        to="/renewals"
                        className="flex items-center p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        Renewals
                      </Link>
                    </li>
                  )}

                  {can('return_debits', 'read') && (
                    <li>
                      <Link
                        to="/return-debits"
                        className="flex items-center p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        Return Debits
                      </Link>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}

          {/* Insurers Dropdown */}
          {can('insurers', 'read') && (
            <li>
              <button
                onClick={toggleInsurersDropdown}
                className="flex items-center justify-between w-full p-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Insurers
                {isInsurersDropdownOpen ? (
                  <ChevronUpIcon className="w-5 h-5" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5" />
                )}
              </button>
              {isInsurersDropdownOpen && (
                <ul className="ml-4 mt-2 space-y-1">
                  <li>
                    <Link
                      to="/insurers/administrators"
                      className="block p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                    >
                      Administrators
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/underwriters"
                      className="block p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                    >
                      Underwriters
                    </Link>
                  </li>
                </ul>
              )}
            </li>
          )}

          {/* More Dropdown */}
          {(can('settings', 'read') ||
            can('process_definitions', 'read') ||
            can('reports', 'read')) && (
            <li>
              <button
                onClick={toggleDropdown}
                className="flex items-center justify-between w-full p-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                More
                {isDropdownOpen ? (
                  <ChevronUpIcon className="w-5 h-5" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5" />
                )}
              </button>
              {isDropdownOpen && (
                <ul className="ml-4 mt-2 space-y-1">
                  {can('settings', 'read') && (
                    <li>
                      <Link
                        to="/settings"
                        className="block p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        Settings
                      </Link>
                    </li>
                  )}

                  {can('process_definitions', 'read') && (
                    <li>
                      <Link
                        to="/process-definitions"
                        className="block p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        Process Definitions
                      </Link>
                    </li>
                  )}

                  {/* Hidden per stakeholder request — uncomment to restore
                  {can('brokerage_details', 'read') && (
                    <li>
                      <Link
                        to="/brokerage-details"
                        className="block p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        Brokerage Details
                      </Link>
                    </li>
                  )}
                  */}

                  {can('reports', 'read') && (
                    <li>
                      <Link
                        to="/reports"
                        className="block p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        Reports
                      </Link>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}
        </ul>
      </nav>
      <div className="mt-auto p-4">
        <img
          src={logoImage}
          alt="Logo"
          className="max-w-full h-auto"
          onError={handleImageError}
          onLoad={() => handleImageLoad('Logo')}
        />
      </div>
    </div>
  );
};

export default Sidebar;